import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { connectFunctionsEmulator } from 'firebase/functions'
import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import GrooverText from '../components/GrooverText'
import { usePortalAuth } from '../hooks/usePortalAuth'
import { auth, functions } from '../libs/Firebase'
import { redeemPortalViewAs } from '../libs/CloudFunctionsApiHandler'
import { hasSuperAdminRole } from '../libs/PortalAuthHelper'
import {
  clearPendingPortalViewAsTicketId,
  clearPendingPortalViewAsToken,
  consumeViewAsTokenFromLocation,
  shouldUseLocalFunctionsFromSearch,
  writePendingPortalViewAsTicketId
} from '../libs/portalViewAsSession'

const waitForAuthUser = (): Promise<typeof auth.currentUser> =>
  new Promise((resolve) => {
    const current = auth.currentUser
    if (current) {
      resolve(current)
      return
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe()
      resolve(user)
    })
  })

const connectLocalFunctionsIfRequested = (search: string): void => {
  if (!shouldUseLocalFunctionsFromSearch(search)) {
    return
  }
  try {
    connectFunctionsEmulator(functions, window.location.hostname || 'localhost', 5001)
  } catch {
    // already connected
  }
}

let viewAsLandingInFlight = false

export const PortalViewAsLandingPage: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const { activateViewAsSession } = usePortalAuth()
  const [error, setError] = useState(false)

  useEffect(() => {
    if (viewAsLandingInFlight) {
      return
    }
    viewAsLandingInFlight = true

    const customToken = consumeViewAsTokenFromLocation(window.location.hash)
    connectLocalFunctionsIfRequested(window.location.search)
    window.history.replaceState(null, '', window.location.pathname)

    const redeem = async () => {
      if (!ticketId) {
        setError(true)
        viewAsLandingInFlight = false
        return
      }

      writePendingPortalViewAsTicketId(ticketId)
      const fail = () => {
        clearPendingPortalViewAsTicketId()
        clearPendingPortalViewAsToken()
        viewAsLandingInFlight = false
        setError(true)
      }

      try {
        let firebaseUser = auth.currentUser
        if (firebaseUser) {
          const token = await firebaseUser.getIdTokenResult()
          const isSuperAdmin = hasSuperAdminRole(token.claims as { roles?: unknown })
          if (!isSuperAdmin) {
            await signOut(auth)
            firebaseUser = null
          }
        }

        if (!firebaseUser) {
          if (!customToken) {
            fail()
            return
          }
          await signInWithCustomToken(auth, customToken)
          firebaseUser = await waitForAuthUser()
        }

        if (!firebaseUser) {
          fail()
          return
        }

        await firebaseUser.getIdToken(true)
        const token = await firebaseUser.getIdTokenResult()
        if (!hasSuperAdminRole(token.claims as { roles?: unknown })) {
          await signOut(auth)
          fail()
          return
        }

        clearPendingPortalViewAsToken()
        const session = await redeemPortalViewAs({ ticketId })
        activateViewAsSession({
          ticketId: session.ticketId,
          enrollmentUserId: session.enrollmentUserId,
          expiresAt: session.expiresAt,
          ...(session.clientId ? { clientId: session.clientId } : {})
        })
        navigate(buildRoute(RoutePath.PORTAL_DASHBOARD), { replace: true })
        viewAsLandingInFlight = false
      } catch {
        fail()
      }
    }

    void redeem()
  }, [activateViewAsSession, navigate, ticketId])

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <GrooverText textKey="portalViewAsRedeemError" />
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <GrooverText textKey="portalViewAsBanner" />
    </div>
  )
}
